package br.com.texeratx.boardgametimeapp.ui.screens.active_match

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.ArrowBack
import androidx.compose.material3.Button
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import br.com.texeratx.boardgametimeapp.core.i18n.LocalStrings
import br.com.texeratx.boardgametimeapp.core.spotify.isSpotifySupportedOnCurrentPlatform
import br.com.texeratx.boardgametimeapp.ui.components.ExpansionCheckRow
import br.com.texeratx.boardgametimeapp.ui.components.GameDropdownSelector
import br.com.texeratx.boardgametimeapp.ui.components.PlayerAvatar
import br.com.texeratx.boardgametimeapp.ui.components.PlayerCheckRow
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppScreenScaffold
import br.com.texeratx.boardgametimeapp.ui.components.designsystem.AppTopBarColors
import br.com.texeratx.boardgametimeapp.ui.spotifydevices.SpotifyDevicePicker
import br.com.texeratx.boardgametimeapp.ui.spotifydevices.SpotifyDevicesUiState
import br.com.texeratx.boardgametimeapp.ui.spotifydevices.SpotifyDevicesViewModel
import cafe.adriel.voyager.core.screen.Screen
import cafe.adriel.voyager.koin.getScreenModel
import cafe.adriel.voyager.navigator.LocalNavigator
import cafe.adriel.voyager.navigator.currentOrThrow

class StartMatchSetupScreen : Screen {

    @Composable
    override fun Content() {
        val S = LocalStrings.current
        val navigator = LocalNavigator.currentOrThrow
        val viewModel = runCatching { getScreenModel<StartMatchSetupViewModel>() }
            .onFailure { it.printStackTrace() }
            .getOrNull()

        if (viewModel == null) {
            Text(S.errorViewModelCreation)
            return
        }

        val state by viewModel.state.collectAsState()

        val spotifyDevicesVm = getScreenModel<SpotifyDevicesViewModel>()
        val spotifyState by spotifyDevicesVm.state.collectAsState()
        val spotifySupported = isSpotifySupportedOnCurrentPlatform()

        val gameHasSpotify = state.selectedGameId?.let { id ->
            state.games.firstOrNull { it.id == id }?.spotifyUrl?.isNotBlank()
        } ?: false

        // Carrega ao abrir
        LaunchedEffect(Unit) {
            viewModel.load()
            if (spotifySupported) {
                spotifyDevicesVm.refreshDevices()
            }
        }

        StartMatchSetupUI(
            state = state,
            onBack = { navigator.pop() },
            onSelectGame = viewModel::selectGame,
            onTogglePlayer = viewModel::togglePlayer,
            onToggleExpansion = viewModel::toggleExpansion,
            onStart = { viewModel.startMatch(navigator, spotifyState.selectedDeviceId) },
            spotifyState = spotifyState,
            onRefreshSpotifyDevices = spotifyDevicesVm::refreshDevices,
            onSelectSpotifyDevice = spotifyDevicesVm::selectDevice,
            spotifySupported = spotifySupported,
            gameHasSpotify = gameHasSpotify,
        )
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun StartMatchSetupUI(
    state: StartMatchSetupUiState,
    onBack: () -> Unit,
    onSelectGame: (Long) -> Unit,
    onTogglePlayer: (Long) -> Unit,
    onToggleExpansion: (Long) -> Unit,
    onStart: () -> Unit,
    spotifyState: SpotifyDevicesUiState,
    onRefreshSpotifyDevices: () -> Unit,
    onSelectSpotifyDevice: (String?) -> Unit,
    spotifySupported: Boolean,
    gameHasSpotify: Boolean,
) {
    val S = LocalStrings.current

    AppScreenScaffold(
        topBar = {
            TopAppBar(
                title = { Text(S.startMatchSetupTitle, fontWeight = FontWeight.Bold) },
                navigationIcon = {
                    IconButton(onClick = onBack) {
                        Icon(Icons.Default.ArrowBack, contentDescription = S.back)
                    }
                },
                colors = AppTopBarColors()
            )
        }
    ) { padding ->

        Column(
            modifier = Modifier
                .padding(padding)
                .padding(16.dp)
                .fillMaxSize()
                .verticalScroll(rememberScrollState()),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {

            if (state.isLoading) {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.Center
                ) {
                    CircularProgressIndicator()
                }
            }

            // Seleção de jogo
            Text(S.sectionGame, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            GameDropdownSelector(
                label = S.labelGame,
                games = state.games,
                selectedGameId = state.selectedGameId,
                onSelect = onSelectGame,
                enabled = !state.isLoading
            )

            Spacer(Modifier.height(8.dp))

            // Seleção de jogadores
            Text(S.sectionPlayers, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

            val selectedPlayers = state.players.filter { state.selectedPlayerIds.contains(it.id) }

            if (selectedPlayers.isNotEmpty()) {
                androidx.compose.foundation.lazy.LazyRow(
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                    modifier = Modifier.fillMaxWidth()
                ) {
                    items(selectedPlayers.size) { index ->
                        val p = selectedPlayers[index]
                        PlayerAvatar(
                            imageUrl = p.imageUrl,
                            contentDescription = p.name,
                            modifier = Modifier.size(38.dp)
                        )
                    }
                }
            }

            if (state.players.isEmpty() && !state.isLoading) {
                Text(S.noPlayersFound)
            } else {
                state.players.forEach { player ->
                    PlayerCheckRow(
                        player = player,
                        checked = state.selectedPlayerIds.contains(player.id),
                        onToggle = { onTogglePlayer(player.id) }
                    )
                }
            }

            Spacer(Modifier.height(2.dp))

            // Seleção de expansões (aparece só quando jogo foi escolhido)
            if (state.selectedGameId != null) {
                Text(S.sectionExpansionsOptional, style = MaterialTheme.typography.titleMedium, fontWeight = FontWeight.Bold)

                if (state.expansions.isEmpty() && !state.isLoading) {
                    Text(S.noExpansionsRegistered)
                } else {
                    state.expansions.forEach { exp ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            ExpansionCheckRow(
                                name = exp.name,
                                checked = state.selectedExpansionIds.contains(exp.id),
                                onToggle = { onToggleExpansion(exp.id) }
                            )
                        }
                    }
                }
            }

            state.error?.let {
                Text(it, color = MaterialTheme.colorScheme.error)
            }

            Spacer(Modifier.height(8.dp))

            if (gameHasSpotify) {
                SpotifyDevicePicker(
                    state = spotifyState,
                    onRefresh = onRefreshSpotifyDevices,
                    onSelect = onSelectSpotifyDevice,
                    enabled = spotifySupported,
                    disabledMessage = if (spotifySupported) null else S.spotifyOnlyInApp
                )
            }

            Spacer(Modifier.height(12.dp))

            Button(
                onClick = onStart,
                enabled = state.selectedGameId != null && state.selectedPlayerIds.isNotEmpty(),
                modifier = Modifier.fillMaxWidth()
            ) {
                Text(S.startMatch)
            }
        }
    }
}
